'use client'

import { Button } from '@/lib/client/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/lib/client/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/lib/client/components/ui/form'
import { Input } from '@/lib/client/components/ui/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { EmailSchema, PasswordSchema } from '@/lib/shared/validations/userSchemas'
import { useSignupMutation } from '@/lib/client/hooks/useSignupMutaion'

// Signup form schema definition
const signupSchema = z
    .object({
        email: EmailSchema,
        password: PasswordSchema,
        confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: 'Passwords do not match',
        path: ['confirmPassword'],
    })

export const SignupForm = () => {
    const router = useRouter()

    const signupMutation = useSignupMutation(async () => {
        router.push('/')
    })
    // React Hook Form setup
    const form = useForm<z.infer<typeof signupSchema>>({
        resolver: zodResolver(signupSchema),
        defaultValues: {
            email: '',
            password: '',
            confirmPassword: '',
        },
    })

    // Signup form submission handler
    const onSubmit = async (data: z.infer<typeof signupSchema>) => {
        signupMutation.mutate(data)
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <Card>
                    <CardHeader className="text-center">
                        <h2 className="text-xl font-semibold">Enter User Information</h2>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Email</FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="example@example.com"
                                            type="email"
                                            autoComplete="email"
                                            disabled={signupMutation.isPending}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="password"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Password</FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="********"
                                            type="password"
                                            autoComplete="new-password"
                                            disabled={signupMutation.isPending}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="confirmPassword"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Confirm Password</FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="********"
                                            type="password"
                                            autoComplete="new-password"
                                            disabled={signupMutation.isPending}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {form.formState.errors.root && (
                            <div className="text-red-500 text-sm font-medium">{form.formState.errors.root.message}</div>
                        )}
                    </CardContent>

                    <CardFooter className="flex flex-col gap-2">
                        <Button type="submit" className="w-full" disabled={signupMutation.isPending}>
                            {signupMutation.isPending ? 'Processing...' : 'Sign Up'}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            onClick={() => router.push('/')}
                            disabled={signupMutation.isPending}
                        >
                            Cancel
                        </Button>
                    </CardFooter>
                </Card>
            </form>
        </Form>
    )
}
